import Button from 'devextreme-react/button';
import DataGrid, {
  Column,
  ColumnChooser,
  HeaderFilter,
  Item,
  Paging,
  SearchPanel,
  Scrolling,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import CheckBox from 'devextreme-react/check-box';
import LoadIndicator from 'devextreme-react/load-indicator';
import CustomStore from 'devextreme/data/custom_store';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  GridDetailField,
  GridDetailFieldsGrid,
  GridDetailPopup,
  GridDetailValue,
  GridFormPopup,
  OPS_SITE_GRID_PROPS,
  OpsGridStateStoring,
  OpsGridToolbarItems,
  onGridFocusedRowEnter,
  onGridRowDblClick,
  opsGridDetailPopupSizeKey,
  opsGridFormPopupSizeKey
} from '../opsDataGrid';
import { showDeleteConfirmDialog } from '../dialogs/confirmDialog';
import { showToast } from '../notifications/toast';
import type {
  StorageMountDto,
  StorageMountDetailDto,
  StorageMountKind,
  StorageAccessMode,
  StorageMountSettingsViewDto,
  UpsertAccessRequest,
  SmbMountSettings,
  S3MountSettings,
  DirectoryPrincipal,
  MentionPeerCandidate,
  MentionScopePreview,
  StorageMountsAdminPageProps
} from './types';
import { normalizeSmbUncPath } from './smbUncPath';
import './StorageMountsAdminPage.css';

const fieldLabel: CSSProperties = {
  fontWeight: 600,
  marginBottom: 6,
  fontSize: 13,
  display: 'block',
  color: 'var(--ops-color-text, #111827)'
};
const hintStyle: CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: 'var(--ops-color-muted, #6b7280)',
  marginTop: 3
};

const fieldHintsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  marginTop: 3
};

const KIND_ITEMS: Array<{ value: StorageMountKind; text: string }> = [
  { value: 'Smb', text: 'SMB / Network Share' },
  { value: 'S3', text: 'S3 / MinIO Bucket' }
];

const MODE_ITEMS: Array<{ value: StorageAccessMode; text: string }> = [
  { value: 'Read', text: 'Read only' },
  { value: 'ReadWrite', text: 'Read + Write' }
];

const KIND_LABEL: Record<StorageMountKind, string> = {
  Smb: 'SMB',
  S3: 'S3 / MinIO'
};

function formatUpdatedAt(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function formatAuditLine(at: string | null | undefined, by: string | null | undefined): string {
  const when = formatUpdatedAt(at);
  if (!by) return when;
  return `${when} · ${by}`;
}

function formatAccessSummary(mount: StorageMountDto): string {
  if (mount.access.length === 0) return 'No bindings';
  if (mount.access.length === 1) {
    const a = mount.access[0];
    const group = a.groupDisplayName ? ` · ${a.groupDisplayName}` : '';
    return `${a.role}${group} (${a.mode === 'ReadWrite' ? 'R/W' : 'Read'})`;
  }
  return `${mount.access.length} bindings`;
}

const EMPTY_SETTINGS: StorageMountSettingsViewDto = {
  uncPath: null,
  domain: null,
  username: null,
  passwordConfigured: false,
  serviceUrl: null,
  region: null,
  bucketName: null,
  forcePathStyle: null,
  accessKeyId: null,
  secretAccessKeyConfigured: false,
  keyPrefix: null,
};

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function normalizeSettings(raw: unknown): StorageMountSettingsViewDto {
  if (!raw || typeof raw !== 'object') return EMPTY_SETTINGS;
  const s = raw as Record<string, unknown>;
  return {
    uncPath: stringOrNull(s.uncPath ?? s.UncPath),
    domain: stringOrNull(s.domain ?? s.Domain),
    username: stringOrNull(s.username ?? s.Username),
    passwordConfigured: s.passwordConfigured === true || s.PasswordConfigured === true,
    serviceUrl: stringOrNull(s.serviceUrl ?? s.ServiceUrl),
    region: stringOrNull(s.region ?? s.Region),
    bucketName: stringOrNull(s.bucketName ?? s.BucketName),
    forcePathStyle:
      typeof s.forcePathStyle === 'boolean'
        ? s.forcePathStyle
        : typeof s.ForcePathStyle === 'boolean'
          ? s.ForcePathStyle
          : null,
    accessKeyId: stringOrNull(s.accessKeyId ?? s.AccessKeyId),
    secretAccessKeyConfigured:
      s.secretAccessKeyConfigured === true || s.SecretAccessKeyConfigured === true,
    keyPrefix: stringOrNull(s.keyPrefix ?? s.KeyPrefix),
  };
}

function normalizeMountKind(value: unknown): StorageMountKind {
  if (value === 'Smb' || value === 'SMB') return 'Smb';
  return 'S3';
}

function kindBadgeClass(kind: StorageMountKind): string {
  return kind === 'Smb' ? 'storage-mount-kind-badge--smb' : 'storage-mount-kind-badge--s3';
}

function accessModeClass(mode: StorageAccessMode | string | null | undefined): string {
  return String(mode ?? 'Read').toLowerCase() === 'readwrite'
    ? 'storage-mount-detail-access-mode--readwrite'
    : 'storage-mount-detail-access-mode--read';
}

function toDetailFromList(mount: StorageMountDto): StorageMountDetailDto {
  return normalizeMountDetail({ ...mount, settings: EMPTY_SETTINGS });
}

function normalizeMountDetail(raw: Partial<StorageMountDetailDto> & Partial<StorageMountDto>): StorageMountDetailDto {
  return {
    id: raw.id ?? '',
    displayName: raw.displayName ?? 'Unknown mount',
    entraTenantId: raw.entraTenantId ?? null,
    kind: normalizeMountKind(raw.kind),
    isActive: raw.isActive ?? false,
    mentionPeerMountId: raw.mentionPeerMountId ?? null,
    createdAt: raw.createdAt ?? new Date(0).toISOString(),
    createdBy: raw.createdBy ?? null,
    updatedAt: raw.updatedAt ?? null,
    updatedBy: raw.updatedBy ?? null,
    access: Array.isArray(raw.access) ? raw.access : [],
    settings: normalizeSettings(raw.settings)
  };
}

interface AccessRow extends UpsertAccessRequest {
  _key: string;
}

function defaultSmbSettings(): SmbMountSettings {
  return { uncPath: '', domain: '', username: '', password: '' };
}

/** Blank S3 form for edit / before saved settings load — no dev MinIO defaults. */
function emptyS3Settings(): S3MountSettings {
  return {
    bucketName: '',
    accessKeyId: '',
    secretAccessKey: '',
    forcePathStyle: false
  };
}

function defaultS3Settings(): S3MountSettings {
  return {
    serviceUrl: 'http://minio:9000',
    bucketName: 'webops',
    accessKeyId: '',
    secretAccessKey: '',
    forcePathStyle: true
  };
}

function getConnectionTestStatus(
  apiSiteLabel: string,
  kind: StorageMountKind,
  smb: SmbMountSettings,
  s3: S3MountSettings,
  editing: StorageMountDto | null,
  saved: StorageMountSettingsViewDto | null
): { ready: boolean; hint: string | null; warning: string | null } {
  // When editing, fall back to saved server values for any field left blank in the form;
  // the API's MergeSettings does the same on save and test endpoints.
  const has = (formValue: string | undefined | null, savedValue: string | null | undefined) =>
    Boolean((formValue ?? '').trim()) || Boolean((savedValue ?? '').toString().trim());

  if (kind === 'Smb') {
    const missing: string[] = [];
    if (!has(smb.uncPath, saved?.uncPath)) missing.push('UNC path');
    if (!has(smb.username, saved?.username)) missing.push('username');
    if (!smb.password?.trim() && !saved?.passwordConfigured) missing.push('password');
    return {
      ready: missing.length === 0,
      hint: missing.length
        ? `Missing: ${missing.join(', ')}.`
        : editing
          ? 'Empty fields will use saved values.'
          : null,
      warning: null
    };
  }

  const missing: string[] = [];
  if (!has(s3.bucketName, saved?.bucketName)) missing.push('bucket name');
  if (!has(s3.accessKeyId, saved?.accessKeyId)) missing.push('access key ID');
  if (!s3.secretAccessKey?.trim() && !saved?.secretAccessKeyConfigured) missing.push('secret access key');

  const url = ((s3.serviceUrl ?? '').trim() || (saved?.serviceUrl ?? '').trim());
  let warning: string | null = null;
  if (/localhost|127\.0\.0\.1/i.test(url)) {
    warning = `localhost is not reachable from the ${apiSiteLabel} API container. For the built-in stack use http://minio:9000.`;
  } else if (/:902[01]\b/.test(url)) {
    warning = `Ports 9020/9021 are host-only MinIO ports. From ${apiSiteLabel} use http://minio:9000.`;
  } else if (/^https?:\/\/10\.0\.3\.20/i.test(url)) {
    warning = 'The API container cannot reach MinIO via the host IP. Use http://minio:9000.';
  } else if (!url && !saved?.serviceUrl) {
    warning = 'Leave Service URL blank only for native AWS S3.';
  } else if (!/^https?:\/\/minio:9000\/?$/i.test(url) && /^https?:\/\/minio/i.test(url)) {
    warning = 'Built-in MinIO listens on port 9000 inside Docker (http://minio:9000).';
  }

  const hint = missing.length
    ? `Missing: ${missing.join(', ')}. Built-in MinIO uses MINIO_ROOT_USER and MINIO_ROOT_PASSWORD from deploy env.`
    : editing
      ? 'Empty fields will use saved values.'
      : null;

  return { ready: missing.length === 0, hint, warning };
}

function serializeSettings(kind: StorageMountKind, smb: SmbMountSettings, s3: S3MountSettings): string {
  if (kind === 'Smb') {
    return JSON.stringify({
      ...smb,
      uncPath: normalizeSmbUncPath(smb.uncPath)
    });
  }
  const payload: S3MountSettings = { ...s3 };
  if (!payload.serviceUrl?.trim()) delete payload.serviceUrl;
  if (!payload.region?.trim()) delete payload.region;
  if (!payload.keyPrefix?.trim()) delete payload.keyPrefix;
  if (!payload.secretAccessKey?.trim()) delete payload.secretAccessKey;
  return JSON.stringify(payload);
}

function pickSavedString(
  formValue: string | undefined | null,
  savedValue: string | null | undefined
): string {
  const form = (formValue ?? '').trim();
  if (form) return form;
  return (savedValue ?? '').trim();
}

/** Merge blank edit fields with saved server values for display, test, and save. */
function resolvedS3Settings(
  form: S3MountSettings,
  saved: StorageMountSettingsViewDto | null,
  editing: StorageMountDto | null
): S3MountSettings {
  if (!editing) return form;
  const serviceUrl = pickSavedString(form.serviceUrl, saved?.serviceUrl);
  const region = pickSavedString(form.region, saved?.region);
  const keyPrefix = pickSavedString(form.keyPrefix, saved?.keyPrefix);
  return {
    serviceUrl: serviceUrl || undefined,
    region: region || undefined,
    bucketName: pickSavedString(form.bucketName, saved?.bucketName),
    accessKeyId: pickSavedString(form.accessKeyId, saved?.accessKeyId),
    keyPrefix: keyPrefix || undefined,
    secretAccessKey: form.secretAccessKey,
    forcePathStyle: form.forcePathStyle ?? saved?.forcePathStyle ?? false
  };
}

function resolvedSmbSettings(
  form: SmbMountSettings,
  saved: StorageMountSettingsViewDto | null,
  editing: StorageMountDto | null
): SmbMountSettings {
  if (!editing) return form;
  return {
    uncPath: pickSavedString(form.uncPath, saved?.uncPath),
    domain: pickSavedString(form.domain, saved?.domain),
    username: pickSavedString(form.username, saved?.username),
    password: form.password
  };
}

const savedValueHintStyle: CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: 'var(--ops-color-muted, #6b7280)',
  fontStyle: 'italic'
};

function FieldHints({ children }: { children: ReactNode }) {
  return <div style={fieldHintsStyle}>{children}</div>;
}

function SavedValueHint({
  show,
  loading,
  value
}: {
  show: boolean;
  loading: boolean;
  value: string | null | undefined;
}) {
  if (!show) return null;
  if (loading) return <span style={savedValueHintStyle}>Loading saved value…</span>;
  return (
    <span style={savedValueHintStyle}>
      {value ? `Saved: ${value}` : 'No saved value — please enter.'}
    </span>
  );
}

const STORAGE_MOUNTS_GRID_SCHEMA_VERSION = 1;
const STORAGE_MOUNTS_GRID_COLUMNS = ['displayName', 'kind', 'isActive'] as const;

function MentionScopeSummary({ preview }: { preview: MentionScopePreview }) {
  if (!preview.mentionsEnabled) {
    return (
      <span style={{ fontSize: 13, color: 'var(--dx-color-warning, #b45309)' }}>
        No Entra groups resolve here, so @mentions are unavailable. Add a group to this mount&apos;s access
        bindings or link a peer mount that has one.
      </span>
    );
  }

  return (
    <div style={{ fontSize: 13 }}>
      <div style={{ marginBottom: 4 }}>
        {preview.peerMountDisplayName
          ? <>Scope comes from <strong>{preview.peerMountDisplayName}</strong>.</>
          : <>Scope comes from this mount&apos;s own access bindings.</>}
        {preview.entraTenantId && (
          <span style={{ color: 'var(--ops-color-muted, #6b7280)' }}> Tenant {preview.entraTenantId}.</span>
        )}
      </div>
      <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
        {preview.groups.map((g) => (
          <li key={g.groupObjectId}>
            {g.groupDisplayName ?? g.groupObjectId}
            {g.groupDisplayName && (
              <span style={{ color: 'var(--ops-color-muted, #6b7280)' }}> ({g.groupObjectId})</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function StorageMountsAdminPage({
  api,
  copy,
  stateStorageKey,
  headerSlot,
  mentionPeerLink
}: StorageMountsAdminPageProps) {
  const gridRef = useRef<DataGridRef>(null);
  const {
    listMounts,
    getMount,
    createMount,
    updateMount,
    deleteMount,
    testMount,
    testMountConnection,
    listRoles,
    searchGroups
  } = api;
  const [mounts, setMounts] = useState<StorageMountDto[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [editing, setEditing] = useState<StorageMountDto | null>(null);
  const [viewMount, setViewMount] = useState<StorageMountDetailDto | null>(null);
  const [viewSettingsLoading, setViewSettingsLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  const [viewTestResult, setViewTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [viewTestLoading, setViewTestLoading] = useState(false);
  const viewRequestRef = useRef(0);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  // Form fields
  const [displayName, setDisplayName] = useState('');
  const [kind, setKind] = useState<StorageMountKind>('S3');
  const [isActive, setIsActive] = useState(true);
  const [smbSettings, setSmbSettings] = useState<SmbMountSettings>(defaultSmbSettings());
  const [s3Settings, setS3Settings] = useState<S3MountSettings>(defaultS3Settings());
  const [connectionDirty, setConnectionDirty] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savedSettings, setSavedSettings] = useState<StorageMountSettingsViewDto | null>(null);
  const [accessRows, setAccessRows] = useState<AccessRow[]>([]);

  // Cross-site mention pairing (only rendered when the site opts in via mentionPeerLink)
  const [peerMountId, setPeerMountId] = useState<string | null>(null);
  const [syncPeerMount, setSyncPeerMount] = useState(false);
  const [peerCandidates, setPeerCandidates] = useState<MentionPeerCandidate[]>([]);
  const [scopePreview, setScopePreview] = useState<MentionScopePreview | null>(null);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopePath, setScopePath] = useState('');

  // Access row form
  const [accRole, setAccRole] = useState<string>('');
  const [accMode, setAccMode] = useState<StorageAccessMode>('Read');
  const [accGroup, setAccGroup] = useState<DirectoryPrincipal | null>(null);
  const groupCacheRef = useRef(new Map<string, DirectoryPrincipal>());

  const groupStore = useMemo(() => new CustomStore({
    key: 'id',
    load: async (loadOptions) => {
      const q = String(loadOptions.searchValue ?? '').trim();
      if (q.length < 2) return [];
      try {
        const results = await searchGroups(q);
        for (const g of results) groupCacheRef.current.set(g.id, g);
        return results;
      } catch {
        return [];
      }
    },
    byKey: async (key) => {
      const cached = groupCacheRef.current.get(String(key));
      if (cached) return cached;
      // Fallback stub — avoids DevExtreme errors if cache was cleared mid-render
      return { id: String(key), displayName: String(key), mail: null, userPrincipalName: null };
    },
  }), [searchGroups]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([listMounts(), listRoles()])
      .then(([m, r]) => { setMounts(m); setRoles(r); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [listMounts, listRoles]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (error) showToast(error, 'error');
  }, [error]);

  const resetGridLayout = useCallback(() => {
    gridRef.current?.instance()?.state(null);
    try {
      localStorage.removeItem(stateStorageKey);
    } catch {
      /* localStorage may be unavailable */
    }
    showToast('Grid layout reset to defaults.', 'info');
  }, [stateStorageKey]);

  const refreshMentionScope = useCallback(
    (mountId: string, path: string) => {
      if (!mentionPeerLink) return;
      setScopeLoading(true);
      mentionPeerLink
        .getMentionScope(mountId, path.trim() || undefined)
        .then(setScopePreview)
        .catch(() => setScopePreview(null))
        .finally(() => setScopeLoading(false));
    },
    [mentionPeerLink]
  );

  function resetMentionPeerForm() {
    setPeerMountId(null);
    setSyncPeerMount(false);
    setScopePreview(null);
    setScopeLoading(false);
    setScopePath('');
  }

  function openCreate() {
    setEditing(null);
    setDisplayName('');
    setKind('S3');
    setIsActive(true);
    setSmbSettings(defaultSmbSettings());
    setS3Settings(defaultS3Settings());
    setConnectionDirty(true);
    setSettingsLoading(false);
    setSavedSettings(null);
    setAccessRows([]);
    resetAccessForm();
    resetMentionPeerForm();
    setTestResult(null);
    setPopupOpen(true);
  }

  function applyDetailSettings(detail: StorageMountDetailDto) {
    if (detail.kind === 'Smb') {
      setSmbSettings({
        uncPath: detail.settings.uncPath ?? '',
        domain: detail.settings.domain ?? '',
        username: detail.settings.username ?? '',
        password: '',
      });
    } else {
      setS3Settings({
        serviceUrl: detail.settings.serviceUrl ?? '',
        region: detail.settings.region ?? '',
        bucketName: detail.settings.bucketName ?? '',
        accessKeyId: detail.settings.accessKeyId ?? '',
        forcePathStyle: detail.settings.forcePathStyle ?? false,
        keyPrefix: detail.settings.keyPrefix ?? '',
        secretAccessKey: ''
      });
    }
    setSavedSettings(detail.settings);
    setConnectionDirty(false);
  }

  function openEdit(mount: StorageMountDto) {
    setViewMount(null);
    setViewError(null);
    setEditing(mount);
    setDisplayName(mount.displayName);
    setKind(mount.kind);
    setIsActive(mount.isActive);
    // Load non-secret settings from the server; secrets stay blank until save/test merge.
    setSmbSettings(defaultSmbSettings());
    setS3Settings(emptyS3Settings());
    setConnectionDirty(false);
    setSavedSettings(null);
    setAccessRows(
      mount.access.map((a) => ({
        _key: a.id,
        role: a.role,
        groupObjectId: a.groupObjectId ?? undefined,
        groupDisplayName: a.groupDisplayName ?? undefined,
        mode: a.mode
      }))
    );
    resetAccessForm();
    resetMentionPeerForm();
    setTestResult(null);
    setPopupOpen(true);

    if (mentionPeerLink) {
      setPeerMountId(mount.mentionPeerMountId);
      mentionPeerLink
        .listPeerCandidates()
        .then(setPeerCandidates)
        .catch(() => setPeerCandidates([]));
      refreshMentionScope(mount.id, '');
    }

    if ('settings' in mount) {
      applyDetailSettings(normalizeMountDetail(mount as StorageMountDetailDto));
      return;
    }

    setSettingsLoading(true);
    void getMount(mount.id)
      .then((detail) => applyDetailSettings(normalizeMountDetail(detail)))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setSettingsLoading(false));
  }

  function closeView() {
    viewRequestRef.current += 1;
    setViewMount(null);
    setViewError(null);
    setViewTestResult(null);
    setViewSettingsLoading(false);
  }

  async function openView(mount: StorageMountDto) {
    const requestId = ++viewRequestRef.current;
    setViewError(null);
    setViewTestResult(null);
    setViewMount(toDetailFromList(mount));
    setViewSettingsLoading(true);
    try {
      const detail = await getMount(mount.id);
      if (requestId !== viewRequestRef.current) return;
      setViewMount(normalizeMountDetail(detail));
    } catch (e: unknown) {
      if (requestId !== viewRequestRef.current) return;
      setViewError(e instanceof Error ? e.message : String(e));
    } finally {
      if (requestId === viewRequestRef.current) {
        setViewSettingsLoading(false);
      }
    }
  }

  function beginEditFromView() {
    if (!viewMount) return;
    openEdit(viewMount);
  }

  async function handleDeleteFromView() {
    if (!viewMount) return;
    const id = viewMount.id;
    if (!(await showDeleteConfirmDialog(`Delete mount "${viewMount.displayName}"? This cannot be undone.`)))
      return;
    try {
      await deleteMount(id);
      closeView();
      load();
    } catch (e: unknown) {
      setViewError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleTestFromView() {
    if (!viewMount) return;
    setViewTestLoading(true);
    setViewTestResult(null);
    try {
      const r = await testMount(viewMount.id);
      setViewTestResult({
        success: r.success,
        message: r.success
          ? `Connected — ${r.itemCount ?? 0} item${r.itemCount === 1 ? '' : 's'} at root.`
          : (r.error ?? 'Connection failed.')
      });
    } catch (e: unknown) {
      setViewTestResult({ success: false, message: e instanceof Error ? e.message : String(e) });
    } finally {
      setViewTestLoading(false);
    }
  }

  function renderViewContent() {
    if (!viewMount) return null;

    const m = viewMount;
    const s = m.settings;

    return (
      <>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ops-color-text, #111827)' }}>
            {m.displayName}
          </div>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span className={`storage-mount-kind-badge ${kindBadgeClass(m.kind)}`}>
              {KIND_LABEL[m.kind] ?? m.kind}
            </span>
            <span className={m.isActive ? 'storage-mount-status storage-mount-status--active' : 'storage-mount-status storage-mount-status--inactive'}>
              {m.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ops-color-muted, #64748b)', fontFamily: 'monospace' }}>
            {m.id}
          </div>
        </div>

        <TabPanel
          className="ops-grid-detail-tab-panel"
          deferRendering={false}
          animationEnabled={false}
          swipeEnabled={false}
          showNavButtons={false}
        >
          <TabItem title="Connection">
            {viewSettingsLoading ? (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.25rem 0' }}>
                <LoadIndicator visible height={18} width={18} />
                <span style={{ fontSize: 13, color: 'var(--ops-color-muted, #64748b)' }}>Loading connection details…</span>
              </div>
            ) : m.kind === 'Smb' ? (
              <GridDetailFieldsGrid columns={1}>
                <GridDetailField label="UNC path" muted={!s.uncPath}>
                  {s.uncPath ?? 'Not configured'}
                </GridDetailField>
                <GridDetailFieldsGrid>
                  <GridDetailField label="Domain" muted={!s.domain}>
                    {s.domain ?? '—'}
                  </GridDetailField>
                  <GridDetailField label="Username" muted={!s.username}>
                    {s.username ?? '—'}
                  </GridDetailField>
                </GridDetailFieldsGrid>
                <GridDetailField label="Password" muted={!s.passwordConfigured}>
                  {s.passwordConfigured ? 'Configured (stored encrypted)' : 'Not configured'}
                </GridDetailField>
              </GridDetailFieldsGrid>
            ) : (
              <GridDetailFieldsGrid columns={1}>
                <GridDetailFieldsGrid>
                  <GridDetailField label="Service URL" muted={!s.serviceUrl}>
                    {s.serviceUrl ?? 'AWS S3 (default endpoint)'}
                  </GridDetailField>
                  <GridDetailField label="Bucket" muted={!s.bucketName}>
                    {s.bucketName ?? '—'}
                  </GridDetailField>
                  <GridDetailField label="Region" muted={!s.region}>
                    {s.region ?? '—'}
                  </GridDetailField>
                  <GridDetailField label="Key prefix" muted={!s.keyPrefix}>
                    {s.keyPrefix ?? 'Bucket root'}
                  </GridDetailField>
                  <GridDetailField label="Access key ID" muted={!s.accessKeyId}>
                    {s.accessKeyId ?? '—'}
                  </GridDetailField>
                  <GridDetailField label="Secret key" muted={!s.secretAccessKeyConfigured}>
                    {s.secretAccessKeyConfigured ? 'Configured (stored encrypted)' : 'Not configured'}
                  </GridDetailField>
                </GridDetailFieldsGrid>
                <GridDetailField label="Path-style URLs">
                  {s.forcePathStyle === false ? 'Disabled' : 'Enabled'}
                </GridDetailField>
              </GridDetailFieldsGrid>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
              <Button
                text="Test connection"
                icon="check"
                onClick={() => void handleTestFromView()}
                disabled={viewTestLoading || viewSettingsLoading}
              />
              {viewTestLoading ? <LoadIndicator visible height={16} width={16} /> : null}
              {viewTestResult ? (
                <span style={{ fontSize: 13, color: viewTestResult.success ? 'var(--dx-color-success, #16a34a)' : 'var(--dx-color-danger, #dc2626)' }}>
                  {viewTestResult.message}
                </span>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--ops-color-muted, #94a3b8)' }}>
                  Tests using saved credentials
                </span>
              )}
            </div>
          </TabItem>
          <TabItem title="Access">
            {m.access.length === 0 ? (
              <GridDetailValue muted>{copy.noBindingsHint}</GridDetailValue>
            ) : (
              <div className="storage-mount-detail-access-list">
                {m.access.map((a) => (
                  <div key={a.id} className="storage-mount-detail-access-row">
                    <span className="storage-mount-detail-access-role">{a.role}</span>
                    <span className="storage-mount-detail-access-group">
                      {a.groupDisplayName ?? (a.groupObjectId ? a.groupObjectId : 'Any group')}
                    </span>
                    <span className={`storage-mount-detail-access-mode ${accessModeClass(a.mode)}`}>
                      {a.mode === 'ReadWrite' ? 'Read + Write' : 'Read only'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </TabItem>
          <TabItem title="Audit">
            <GridDetailFieldsGrid columns={1}>
              <GridDetailField label="Created">{formatAuditLine(m.createdAt, m.createdBy)}</GridDetailField>
              <GridDetailField label="Updated" muted={!m.updatedAt}>
                {formatAuditLine(m.updatedAt ?? m.createdAt, m.updatedBy)}
              </GridDetailField>
            </GridDetailFieldsGrid>
          </TabItem>
        </TabPanel>
      </>
    );
  }

  function resetAccessForm() {
    setAccRole('');
    setAccMode('Read');
    setAccGroup(null);
    groupCacheRef.current.clear();
  }

  function addAccessRow() {
    if (!accRole) return;
    setAccessRows((prev) => [
      ...prev,
      {
        _key: crypto.randomUUID(),
        role: accRole,
        groupObjectId: accGroup?.id ?? undefined,
        groupDisplayName: accGroup?.displayName ?? undefined,
        mode: accMode
      }
    ]);
    resetAccessForm();
  }

  function removeAccessRow(key: string) {
    setAccessRows((prev) => prev.filter((r) => r._key !== key));
  }

  async function handleSave() {
    if (!displayName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const settingsJson =
        !editing || connectionDirty
          ? serializeSettings(kind, effectiveSmbSettings, effectiveS3Settings)
          : undefined;
      const access = accessRows.map(({ _key: _k, ...rest }) => rest);
      if (editing) {
        await updateMount(editing.id, {
          displayName: displayName.trim(),
          settingsJson,
          isActive,
          access
        });
        if (mentionPeerLink && peerMountId !== editing.mentionPeerMountId) {
          await mentionPeerLink.setMentionPeer(editing.id, peerMountId, syncPeerMount);
        }
      } else {
        await createMount({
          displayName: displayName.trim(),
          kind,
          settingsJson: serializeSettings(kind, smbSettings, s3Settings),
          access
        });
      }
      setPopupOpen(false);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!(await showDeleteConfirmDialog('Delete this storage mount? This cannot be undone.'))) return;
    try {
      await deleteMount(id);
      if (viewMount?.id === id) closeView();
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleTest() {
    const activeKind = editing?.kind ?? kind;
    const testStatus = getConnectionTestStatus(
      copy.apiSiteLabel,
      activeKind,
      effectiveSmbSettings,
      effectiveS3Settings,
      editing,
      savedSettings
    );
    if (!testStatus.ready) return;

    setTestLoading(true);
    setTestResult(null);
    try {
      const r = await testMountConnection({
        kind: activeKind,
        settingsJson: serializeSettings(activeKind, effectiveSmbSettings, effectiveS3Settings),
        existingMountId: editing?.id ?? null
      });
      setTestResult({
        success: r.success,
        message: r.success
          ? `Connected — ${r.itemCount ?? 0} item${r.itemCount === 1 ? '' : 's'} at root.`
          : (r.error ?? 'Connection failed.')
      });
    } catch (e: unknown) {
      setTestResult({ success: false, message: e instanceof Error ? e.message : String(e) });
    } finally {
      setTestLoading(false);
    }
  }

  const activeKind = editing?.kind ?? kind;
  const effectiveS3Settings = useMemo(
    () => resolvedS3Settings(s3Settings, savedSettings, editing),
    [s3Settings, savedSettings, editing]
  );
  const effectiveSmbSettings = useMemo(
    () => resolvedSmbSettings(smbSettings, savedSettings, editing),
    [smbSettings, savedSettings, editing]
  );
  const testStatus = getConnectionTestStatus(
    copy.apiSiteLabel,
    activeKind,
    effectiveSmbSettings,
    effectiveS3Settings,
    editing,
    savedSettings
  );
  const connectionFormKey = editing ? `mount-${editing.id}-${settingsLoading ? 'loading' : 'ready'}` : 'create';

  return (
    <div className="ops-fill-page ops-fill-page--bare">
      {headerSlot}

      <div className="ops-fill-page__grid-host">
        {loading ? (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '1rem' }}>
            <LoadIndicator visible height={20} width={20} />
            <span>Loading…</span>
          </div>
        ) : (
            <DataGrid
              ref={gridRef}
              dataSource={mounts}
              keyExpr="id"
              height="100%"
              showBorders
              columnAutoWidth
              columnHidingEnabled
              allowColumnResizing
              allowColumnReordering
              columnResizingMode="widget"
              {...OPS_SITE_GRID_PROPS}
              noDataText="No mounts configured — click Add mount to create one."
              onRowDblClick={(e) => onGridRowDblClick(e, (row) => void openView(row))}
              onKeyDown={(e) => onGridFocusedRowEnter(e, (row) => void openView(row))}
            >
              <OpsGridStateStoring
                storageKey={stateStorageKey}
                schemaVersion={STORAGE_MOUNTS_GRID_SCHEMA_VERSION}
                columns={STORAGE_MOUNTS_GRID_COLUMNS}
              />
              <Toolbar>
                <OpsGridToolbarItems
                  gridRef={gridRef}
                  loading={loading}
                  onRefresh={() => load()}
                  refreshHint="Refresh mounts"
                  newItem={{ hint: 'Add mount', onClick: openCreate }}
                  resetLayout={{ onReset: resetGridLayout }}
                />
                <Item name="searchPanel" location="after" />
              </Toolbar>
              <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
              <SearchPanel visible width={240} placeholder="Search mounts…" />
              <HeaderFilter visible />
              <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
              <Paging enabled={false} />
              <Column name="displayName" dataField="displayName" caption="Name" minWidth={160} />
              <Column
                name="kind"
                dataField="kind"
                caption="Type"
                width={120}
                cellRender={({ value }: { value: StorageMountKind }) => (
                  <span className={`storage-mount-kind-badge storage-mount-kind-badge--${value.toLowerCase()}`}>
                    {KIND_LABEL[value] ?? value}
                  </span>
                )}
              />
              <Column
                caption="Access bindings"
                minWidth={220}
                allowFiltering={false}
                allowHeaderFiltering={false}
                calculateCellValue={(r: StorageMountDto) => formatAccessSummary(r)}
                cellRender={({ data }: { data: StorageMountDto }) => (
                  <span
                    className={data.access.length === 0 ? 'storage-mount-access--empty' : 'storage-mount-access'}
                    title={data.access.map((a) => {
                      const g = a.groupDisplayName ? ` + ${a.groupDisplayName}` : '';
                      return `${a.role}${g} (${a.mode})`;
                    }).join('\n')}
                  >
                    {formatAccessSummary(data)}
                  </span>
                )}
              />
              <Column
                name="isActive"
                dataField="isActive"
                caption="Status"
                dataType="boolean"
                width={100}
                trueText="Active"
                falseText="Inactive"
                cellRender={({ value }: { value: boolean }) => (
                  <span className={value ? 'storage-mount-status storage-mount-status--active' : 'storage-mount-status storage-mount-status--inactive'}>
                    {value ? 'Active' : 'Inactive'}
                  </span>
                )}
              />
              <Column
                caption="Updated"
                allowFiltering={false}
                allowHeaderFiltering={false}
                width={170}
                calculateCellValue={(r: StorageMountDto) => r.updatedAt ?? r.createdAt}
                calculateDisplayValue={(r: StorageMountDto) => formatUpdatedAt(r.updatedAt ?? r.createdAt)}
              />
              <Column
                caption="Actions"
                width={200}
                allowSorting={false}
                allowFiltering={false}
                cellRender={({ data }: { data: StorageMountDto }) => (
                  <div className="storage-mount-actions">
                    <Button text="Edit" icon="edit" stylingMode="text" onClick={() => openEdit(data)} />
                    <Button text="Delete" icon="trash" stylingMode="text" type="danger" onClick={() => handleDelete(data.id)} />
                  </div>
                )}
              />
            </DataGrid>
        )}
      </div>

      <GridDetailPopup
        visible={viewMount !== null}
        title="Storage mount details"
        onClose={closeView}
        width={720}
        maxBodyHeight={520}
        sizeStorageKey={opsGridDetailPopupSizeKey(stateStorageKey)}
        error={viewError}
        primaryActions={[
          {
            text: 'Delete mount',
            icon: 'trash',
            type: 'danger',
            stylingMode: 'outlined',
            onClick: () => void handleDeleteFromView()
          },
          {
            text: 'Edit',
            icon: 'edit',
            type: 'default',
            stylingMode: 'contained',
            onClick: beginEditFromView
          }
        ]}
      >
        {renderViewContent()}
      </GridDetailPopup>

      {/* Mount edit/create popup */}
      <GridFormPopup
        visible={popupOpen}
        title={editing ? `Edit — ${editing.displayName}` : 'Add storage mount'}
        onClose={() => setPopupOpen(false)}
        width={700}
        maxBodyHeight={640}
        sizeStorageKey={opsGridFormPopupSizeKey(stateStorageKey)}
        error={error}
        footer={
          <>
            <Button text="Cancel" onClick={() => setPopupOpen(false)} />
            <Button
              text={saving ? 'Saving…' : editing ? 'Save changes' : 'Create mount'}
              type="default"
              onClick={handleSave}
              disabled={saving || !displayName.trim()}
            />
          </>
        }
      >
        <div key={connectionFormKey} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* Display name */}
            <div>
              <span style={fieldLabel}>Display name *</span>
              <TextBox value={displayName} onValueChanged={(e) => setDisplayName(e.value as string)} placeholder="e.g. HR Share, Sales Bucket…" />
            </div>

            {/* Kind (only on create) */}
            {!editing && (
              <div>
                <span style={fieldLabel}>Storage type *</span>
                <SelectBox
                  dataSource={KIND_ITEMS}
                  displayExpr="text"
                  valueExpr="value"
                  value={kind}
                  onValueChanged={(e) => setKind(e.value as StorageMountKind)}
                />
              </div>
            )}

            {editing && settingsLoading ? (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.25rem 0' }}>
                <LoadIndicator visible height={18} width={18} />
                <span style={{ fontSize: 13, color: 'var(--ops-color-muted, #64748b)' }}>
                  Loading saved connection settings…
                </span>
              </div>
            ) : null}

            {/* SMB settings */}
            {(editing?.kind ?? kind) === 'Smb' && !(editing && settingsLoading) && (
              <>
                <div style={{ fontWeight: 600, borderTop: '1px solid var(--dx-color-border, #e5e7eb)', paddingTop: '0.75rem' }}>
                  SMB / Network Share Settings
                  {editing && (
                    <span style={{ ...hintStyle, display: 'inline', marginLeft: 8 }}>
                      {settingsLoading
                        ? 'Loading saved values…'
                        : 'Leave any field blank to keep its existing value.'}
                    </span>
                  )}
                </div>
                <div>
                  <span style={fieldLabel}>UNC path</span>
                  <TextBox
                    value={effectiveSmbSettings.uncPath}
                    onValueChanged={(e) => {
                      setConnectionDirty(true);
                      setSmbSettings({ ...smbSettings, uncPath: e.value as string });
                    }}
                    placeholder={settingsLoading ? 'Loading saved values…' : '\\\\server\\sharename'}
                    disabled={settingsLoading}
                  />
                  <FieldHints>
                    <span style={{ ...hintStyle, marginTop: 0 }}>e.g. \\\\fileserver\\reports or \\\\fileserver\\reports\\sub</span>
                    <SavedValueHint show={!!editing} loading={settingsLoading} value={savedSettings?.uncPath} />
                  </FieldHints>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <span style={fieldLabel}>Domain</span>
                    <TextBox
                      value={effectiveSmbSettings.domain}
                      onValueChanged={(e) => {
                        setConnectionDirty(true);
                        setSmbSettings({ ...smbSettings, domain: e.value as string });
                      }}
                      placeholder="CONTOSO"
                      disabled={settingsLoading}
                    />
                    <SavedValueHint show={!!editing} loading={settingsLoading} value={savedSettings?.domain} />
                  </div>
                  <div>
                    <span style={fieldLabel}>Username</span>
                    <TextBox
                      value={effectiveSmbSettings.username}
                      onValueChanged={(e) => {
                        setConnectionDirty(true);
                        setSmbSettings({ ...smbSettings, username: e.value as string });
                      }}
                      placeholder="svc-webops"
                      disabled={settingsLoading}
                    />
                    <SavedValueHint show={!!editing} loading={settingsLoading} value={savedSettings?.username} />
                  </div>
                </div>
                <div>
                  <span style={fieldLabel}>Password</span>
                  <TextBox
                    value={smbSettings.password ?? ''}
                    onValueChanged={(e) => {
                      setConnectionDirty(true);
                      setSmbSettings({ ...smbSettings, password: e.value as string });
                    }}
                    mode="password"
                    placeholder={editing ? '(unchanged)' : ''}
                    disabled={settingsLoading}
                  />
                </div>
              </>
            )}

            {/* S3 settings */}
            {(editing?.kind ?? kind) === 'S3' && !(editing && settingsLoading) && (
              <>
                <div style={{ fontWeight: 600, borderTop: '1px solid var(--dx-color-border, #e5e7eb)', paddingTop: '0.75rem' }}>
                  S3 / MinIO Settings
                  {editing && (
                    <span style={{ ...hintStyle, display: 'inline', marginLeft: 8 }}>
                      {settingsLoading
                        ? 'Loading saved values…'
                        : 'Leave any field blank to keep its existing value.'}
                    </span>
                  )}
                </div>
                <div>
                  <span style={fieldLabel}>Service URL</span>
                  <TextBox
                    value={effectiveS3Settings.serviceUrl ?? ''}
                    onValueChanged={(e) => {
                      setConnectionDirty(true);
                      setS3Settings({ ...s3Settings, serviceUrl: (e.value as string) ?? '' });
                    }}
                    placeholder={settingsLoading ? 'Loading saved values…' : 'http://minio:9000'}
                    disabled={settingsLoading}
                  />
                  <FieldHints>
                    <span style={{ ...hintStyle, marginTop: 0 }}>
                      Built-in stack MinIO: <code>http://minio:9000</code> (not host port 9020).
                    </span>
                    <SavedValueHint show={!!editing} loading={settingsLoading} value={savedSettings?.serviceUrl} />
                  </FieldHints>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <span style={fieldLabel}>Bucket name *</span>
                    <TextBox
                      value={effectiveS3Settings.bucketName}
                      onValueChanged={(e) => {
                        setConnectionDirty(true);
                        setS3Settings({ ...s3Settings, bucketName: e.value as string });
                      }}
                      placeholder="webops"
                      disabled={settingsLoading}
                    />
                    <SavedValueHint show={!!editing} loading={settingsLoading} value={savedSettings?.bucketName} />
                  </div>
                  <div>
                    <span style={fieldLabel}>Region</span>
                    <TextBox
                      value={effectiveS3Settings.region ?? ''}
                      onValueChanged={(e) => {
                        setConnectionDirty(true);
                        setS3Settings({ ...s3Settings, region: (e.value as string) ?? '' });
                      }}
                      placeholder="us-east-1 (AWS only)"
                      disabled={settingsLoading}
                    />
                    <SavedValueHint show={!!editing} loading={settingsLoading} value={savedSettings?.region} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <span style={fieldLabel}>Access key ID *</span>
                    <TextBox
                      value={effectiveS3Settings.accessKeyId}
                      onValueChanged={(e) => {
                        setConnectionDirty(true);
                        setS3Settings({ ...s3Settings, accessKeyId: e.value as string });
                      }}
                      placeholder="e.g. webops-prod"
                      disabled={settingsLoading}
                    />
                    <FieldHints>
                      <span style={{ ...hintStyle, marginTop: 0 }}>Same as MINIO_ROOT_USER in deploy env.</span>
                      <SavedValueHint show={!!editing} loading={settingsLoading} value={savedSettings?.accessKeyId} />
                    </FieldHints>
                  </div>
                  <div>
                    <span style={fieldLabel}>Secret access key *</span>
                    <TextBox
                      value={s3Settings.secretAccessKey ?? ''}
                      onValueChanged={(e) => {
                        setConnectionDirty(true);
                        setS3Settings({ ...s3Settings, secretAccessKey: e.value as string || undefined });
                      }}
                      mode="password"
                      placeholder={editing ? '(unchanged)' : 'MINIO_ROOT_PASSWORD'}
                      disabled={settingsLoading}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <span style={fieldLabel}>Key prefix (optional)</span>
                    <TextBox
                      value={effectiveS3Settings.keyPrefix ?? ''}
                      onValueChanged={(e) => {
                        setConnectionDirty(true);
                        setS3Settings({ ...s3Settings, keyPrefix: (e.value as string) ?? '' });
                      }}
                      placeholder="e.g. reports/"
                      disabled={settingsLoading}
                    />
                    <FieldHints>
                      <span style={{ ...hintStyle, marginTop: 0 }}>Limits the browser root to this prefix within the bucket.</span>
                      <SavedValueHint show={!!editing} loading={settingsLoading} value={savedSettings?.keyPrefix} />
                    </FieldHints>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.5rem' }}>
                    <CheckBox
                      value={effectiveS3Settings.forcePathStyle ?? false}
                      onValueChanged={(e) => {
                        setConnectionDirty(true);
                        setS3Settings({ ...s3Settings, forcePathStyle: e.value as boolean });
                      }}
                      text="Force path-style URLs (required for MinIO)"
                      disabled={settingsLoading}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Active toggle (edit only) */}
            {editing && (
              <div>
                <CheckBox value={isActive} onValueChanged={(e) => setIsActive(e.value as boolean)} text="Mount is active (visible in file browser)" />
              </div>
            )}

            {/* Test connection — available for SMB and S3 on create and edit */}
            {(activeKind === 'Smb' || activeKind === 'S3') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <Button
                    text="Test connection"
                    icon="check"
                    onClick={() => void handleTest()}
                    disabled={testLoading || !testStatus.ready || !!(editing && settingsLoading)}
                  />
                  {testLoading && <LoadIndicator visible height={16} width={16} />}
                  {testResult && (
                    <span style={{ fontSize: 13, color: testResult.success ? 'var(--dx-color-success, #16a34a)' : 'var(--dx-color-danger, #dc2626)' }}>
                      {testResult.message}
                    </span>
                  )}
                </div>
                {!testStatus.ready && testStatus.hint && (
                  <span style={{ ...hintStyle, marginTop: 0 }}>{testStatus.hint}</span>
                )}
                {testStatus.warning && (
                  <span style={{ ...hintStyle, marginTop: 0, color: 'var(--dx-color-warning, #b45309)' }}>{testStatus.warning}</span>
                )}
              </div>
            )}

            {/* Access bindings */}
            <div style={{ borderTop: '1px solid var(--dx-color-border, #e5e7eb)', paddingTop: '0.75rem' }}>
              <span style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>Access bindings</span>
              <p style={{ ...hintStyle, marginTop: 0, marginBottom: 12 }}>
                {copy.accessGrantHint}
              </p>

              {/* Existing rows */}
              {accessRows.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  {accessRows.map((row) => (
                    <div key={row._key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '4px 0', borderBottom: '1px solid var(--dx-color-border, #f3f4f6)' }}>
                      <span style={{ flex: 1, fontSize: 13 }}>{row.role}</span>
                      {row.groupDisplayName && <span style={{ flex: 2, fontSize: 12, color: 'var(--ops-color-muted, #6b7280)' }}>{row.groupDisplayName}</span>}
                      <span style={{ fontSize: 12, color: row.mode === 'ReadWrite' ? 'var(--dx-color-success, #16a34a)' : 'inherit' }}>{row.mode}</span>
                      <Button icon="trash" stylingMode="text" type="danger" onClick={() => removeAccessRow(row._key)} />
                    </div>
                  ))}
                </div>
              )}

              {/* Add new access row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr auto auto', gap: '0.75rem', alignItems: 'end' }}>
                <div>
                  <span style={{ ...fieldLabel, marginBottom: 4 }}>Role</span>
                  <SelectBox
                    dataSource={roles}
                    value={accRole || null}
                    onValueChanged={(e) => setAccRole(e.value as string ?? '')}
                    placeholder="Select role…"
                    showClearButton
                  />
                </div>
                <div>
                  <span style={{ ...fieldLabel, marginBottom: 4 }}>Entra group (optional)</span>
                  <SelectBox
                    dataSource={groupStore}
                    displayExpr="displayName"
                    valueExpr="id"
                    searchEnabled
                    minSearchLength={2}
                    searchTimeout={300}
                    placeholder="Type to search groups…"
                    noDataText="No groups found — type at least 2 characters"
                    value={accGroup?.id ?? null}
                    onValueChanged={(e) => {
                      const id = e.value as string | null | undefined;
                      if (!id) {
                        setAccGroup(null);
                        return;
                      }
                      setAccGroup(groupCacheRef.current.get(id) ?? null);
                    }}
                    showClearButton
                  />
                </div>
                <div>
                  <span style={{ ...fieldLabel, marginBottom: 4 }}>Mode</span>
                  <SelectBox
                    dataSource={MODE_ITEMS}
                    displayExpr="text"
                    valueExpr="value"
                    value={accMode}
                    onValueChanged={(e) => setAccMode(e.value as StorageAccessMode)}
                    width={140}
                  />
                </div>
                <Button icon="plus" text="Add" onClick={addAccessRow} disabled={!accRole} style={{ marginBottom: 0 }} />
              </div>
            </div>

            {mentionPeerLink && editing && (
              <div style={{ borderTop: '1px solid var(--dx-color-border, #e5e7eb)', paddingTop: '0.75rem' }}>
                <span style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>Mention pairing</span>
                <p style={{ ...hintStyle, marginTop: 0, marginBottom: 12 }}>
                  @mentions on this mount offer people from the {mentionPeerLink.peerSiteLabel} mount&apos;s Entra
                  groups. Leave the link empty to match on storage location instead.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem', alignItems: 'end' }}>
                  <div>
                    <span style={{ ...fieldLabel, marginBottom: 4 }}>
                      {mentionPeerLink.peerSiteLabel} mount
                    </span>
                    <SelectBox
                      dataSource={peerCandidates}
                      displayExpr={(c: MentionPeerCandidate | null) =>
                        c ? `${c.displayName} — ${c.groupCount} group${c.groupCount === 1 ? '' : 's'}` : ''}
                      valueExpr="id"
                      value={peerMountId}
                      placeholder="Match on storage location…"
                      noDataText={`No active ${mentionPeerLink.peerSiteLabel} mounts found`}
                      onValueChanged={(e) => setPeerMountId((e.value as string | null) ?? null)}
                      showClearButton
                    />
                  </div>
                  <CheckBox
                    text="Link back from the other side too"
                    value={syncPeerMount}
                    onValueChanged={(e) => setSyncPeerMount(Boolean(e.value))}
                    disabled={!peerMountId}
                  />
                </div>

                <div style={{ marginTop: 12 }}>
                  <span style={{ ...fieldLabel, marginBottom: 4 }}>Who this mount can mention</span>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: 8 }}>
                    <TextBox
                      value={scopePath}
                      onValueChanged={(e) => setScopePath(String(e.value ?? ''))}
                      placeholder="Optional file path, e.g. DEMOCE1/reports/q1.pdf"
                      width={340}
                    />
                    <Button
                      text="Check"
                      onClick={() => refreshMentionScope(editing.id, scopePath)}
                      disabled={scopeLoading}
                    />
                    {scopeLoading && <LoadIndicator height={18} width={18} />}
                  </div>
                  <p style={{ ...hintStyle, marginTop: 0, marginBottom: 8 }}>
                    Mounts that sit above a folder per client resolve a different group for each folder, so
                    enter a path to see what a comment on that file would offer.
                  </p>
                  {scopePreview && <MentionScopeSummary preview={scopePreview} />}
                </div>
              </div>
            )}

        </div>
      </GridFormPopup>
    </div>
  );
}
